import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Shield, Lock, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { ThemeToggle } from '@/components/theme-provider';

export default function AdminLogin() {
    const navigate = useNavigate();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [attempts, setAttempts] = useState(0);

    const isAccountLocked = () => {
        const lockUntil = localStorage.getItem('adminLockUntil');
        if (lockUntil) {
            const lockTime = parseInt(lockUntil);
            if (Date.now() < lockTime) {
                return true;
            } else {
                localStorage.removeItem('adminLockUntil');
                localStorage.removeItem('adminAttempts');
            }
        }
        return false;
    };

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (isAccountLocked()) {
            const lockUntil = localStorage.getItem('adminLockUntil');
            const remainingTime = Math.ceil((parseInt(lockUntil!) - Date.now()) / 60000);
            setError(`Account locked. Try again in ${remainingTime} minutes.`);
            return;
        }

        setIsLoading(true);
        await new Promise(resolve => setTimeout(resolve, 1000));

        const ADMIN_USERNAME = 'admin';
        const ADMIN_PASSWORD = 'Admin@2024!Secure';

        if (username === ADMIN_USERNAME && password === ADMIN_PASSWORD) {
            const sessionToken = btoa(`${username}:${Date.now()}:${Math.random()}`);
            const sessionExpiry = Date.now() + (30 * 60 * 1000);

            localStorage.setItem('adminToken', sessionToken);
            localStorage.setItem('adminTokenExpiry', sessionExpiry.toString());
            localStorage.setItem('adminUsername', username);
            localStorage.removeItem('adminAttempts');
            
            const loginLog = JSON.parse(localStorage.getItem('adminLoginLog') || '[]');
            loginLog.push({
                timestamp: new Date().toISOString(),
                status: 'success',
                username
            });
            localStorage.setItem('adminLoginLog', JSON.stringify(loginLog.slice(-50)));

            navigate('/admin/dashboard');
        } else {
            const newAttempts = attempts + 1;
            setAttempts(newAttempts);
            
            const loginLog = JSON.parse(localStorage.getItem('adminLoginLog') || '[]');
            loginLog.push({
                timestamp: new Date().toISOString(),
                status: 'failed',
                username
            });
            localStorage.setItem('adminLoginLog', JSON.stringify(loginLog.slice(-50)));

            if (newAttempts >= 3) {
                const lockUntil = Date.now() + (15 * 60 * 1000);
                localStorage.setItem('adminLockUntil', lockUntil.toString());
                localStorage.setItem('adminAttempts', newAttempts.toString());
                setError('Too many failed attempts. Account locked for 15 minutes.');
            } else {
                setError(`Invalid credentials. ${3 - newAttempts} attempts remaining.`);
            }
        }

        setIsLoading(false);
    };

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
            <div className="absolute top-4 right-4">
                <ThemeToggle />
            </div>

            <Card className="w-full max-w-md p-8">
                <div className="flex flex-col items-center mb-8">
                    <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mb-4">
                        <Shield className="w-8 h-8 text-red-500" />
                    </div>
                    <h1 className="text-2xl font-bold">Admin Portal</h1>
                    <p className="text-sm text-muted-foreground mt-2">High Security Access</p>
                </div>

                {error && (
                    <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                    </div>
                )}

                <form onSubmit={handleLogin} className="space-y-6">
                    <div className="space-y-2">
                        <label className="text-sm font-medium">Username</label>
                        <Input
                            type="text"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            placeholder="Enter admin username"
                            required
                            disabled={isLoading || isAccountLocked()}
                            className="w-full"
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium">Password</label>
                        <div className="relative">
                            <Input
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Enter admin password"
                                required
                                disabled={isLoading || isAccountLocked()}
                                className="w-full pr-10"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            >
                                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                        </div>
                    </div>

                    <Button
                        type="submit"
                        className="w-full"
                        disabled={isLoading || isAccountLocked()}
                    >
                        <Lock className="w-4 h-4 mr-2" />
                        {isLoading ? 'Authenticating...' : 'Secure Login'}
                    </Button>
                </form>

                <div className="mt-6 pt-6 border-t">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate('/')}
                        className="w-full"
                    >
                        Back to Home
                    </Button>
                </div>

                <div className="mt-6 p-4 bg-muted/50 rounded-lg">
                    <p className="text-xs text-muted-foreground text-center">
                        🔒 Secured with encryption • Session timeout: 30 min
                    </p>
                </div>
            </Card>
        </div>
    );
}
